import React from 'react';
import { AlertOctagon, AlertTriangle, CheckCircle2, Info, Loader2, RefreshCw } from 'lucide-react';
import { ProjectCheckResult } from '../../types';
import { Button } from '../Button';
import { ProjectCheckChip } from './InvoiceStatusBadge';
import { allClearText, splitCheckFindings } from './projectCheckPanelLogic';

/**
 * Befunde des Projektchecks am Beleg.
 *
 * Zeigt bewusst ALLE Befunde, nicht nur die sperrenden. Der Zweck des Checks
 * ist Sichtbarkeit: wer nur die roten sieht, korrigiert die gelben nie — und
 * genau die sind die stillen Datendrift-Quellen.
 *
 * Gruene Befunde sind dabei keine Befunde, sondern Hinweiszeilen (Regel 17
 * "Offene Sicherheitseinbehalte"). Sie erscheinen in der Liste, aber neutral
 * und ohne Warnsymbol, und sie zaehlen nicht mit — welche Zeile was ist,
 * entscheidet projectCheckPanelLogic.
 */

interface Props {
    result: ProjectCheckResult | null;
    loading?: boolean;
    error?: string | null;
    /** Pflichtnotiz bei gelber Ampel. */
    acknowledgement: string;
    onAcknowledgementChange: (value: string) => void;
    onRefresh: () => void;
    /** Ausgestellte Belege zeigen den eingefrorenen Befund, ohne Eingabefeld. */
    readOnly?: boolean;
    /**
     * Markiert das Projekt als eines ohne Berichtsfuehrung. Die Aktion steht am
     * Befund, weil der Nutzer genau dort merkt, dass die Regel nicht zu seinem
     * Projekt passt — nicht auf einer Einstellungsseite, die er erst suchen muss.
     */
    onExemptProject?: (exempt: boolean) => void;
    exempting?: boolean;
}

export const ProjectCheckPanel: React.FC<Props> = ({
    result,
    loading,
    error,
    acknowledgement,
    onAcknowledgementChange,
    onRefresh,
    readOnly = false,
    onExemptProject,
    exempting = false,
}) => {
    const { issues, notices } = splitCheckFindings(result?.findings ?? []);

    return (
        <div className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="mb-2 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-slate-700">Projektcheck</h2>
                <div className="flex items-center gap-2">
                    {/* Die Zahl am Chip zaehlt nur Rot und Gelb — sonst
                        meldete er "Grün (1)" fuer eine Hinweiszeile. */}
                    {result && <ProjectCheckChip traffic={result.traffic} count={issues.length} />}
                    {!readOnly && (
                        <button
                            type="button"
                            onClick={onRefresh}
                            disabled={loading}
                            title="Erneut prüfen"
                            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
                        >
                            {loading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <RefreshCw className="h-4 w-4" />
                            )}
                        </button>
                    )}
                </div>
            </div>

            {error && (
                <div className="mb-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                    {error}
                </div>
            )}

            {loading && !result && (
                <p className="py-2 text-xs text-slate-500">Projektdaten werden geprüft …</p>
            )}

            {/* Nur die ECHTEN Befunde entscheiden, ob die Entwarnung faellt: eine
                Hinweiszeile allein ist kein Grund, sie verschwinden zu lassen. */}
            {result && issues.length === 0 && (
                <p className="flex items-center gap-1.5 py-1 text-xs text-emerald-700">
                    <CheckCircle2 className="h-4 w-4 shrink-0" />
                    {allClearText(result, notices.length > 0)}
                </p>
            )}

            {/* Ein schweigender Check sieht aus wie ein bestandener. Wer spaeter auf
                dieses Projekt schaut, muss erkennen koennen, dass hier zwei Regeln
                stillgelegt wurden — und den Weg zurueck finden. */}
            {result?.reportExempt && (
                <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">
                    <p>
                        Dieses Projekt ist als <strong>Projekt ohne Berichtsführung</strong> markiert.
                        Der Abgleich von Berichtsarchiv und Begehungszähler entfällt hier dauerhaft;
                        alle übrigen Prüfungen laufen weiter.
                    </p>
                    {!readOnly && onExemptProject && (
                        <button
                            type="button"
                            onClick={() => onExemptProject(false)}
                            disabled={exempting}
                            className="mt-1.5 inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                        >
                            {exempting && <Loader2 className="h-3 w-3 animate-spin" />}
                            Markierung aufheben und wieder prüfen
                        </button>
                    )}
                </div>
            )}

            {result && result.findings.length > 0 && (
                <ul className={`space-y-1.5 ${issues.length === 0 ? 'mt-2' : ''}`}>
                    {result.findings.map((f) => (
                        <li
                            key={f.id}
                            className={`rounded-md border p-2 text-xs ${
                                f.traffic === 'red'
                                    ? 'border-red-200 bg-red-50'
                                    : f.traffic === 'yellow'
                                      ? 'border-amber-200 bg-amber-50'
                                      : // Gruen = Hinweiszeile: dieselbe neutrale
                                        // Schale wie der Vermerk "Projekt ohne
                                        // Berichtsfuehrung" darueber.
                                        'border-slate-200 bg-slate-50'
                            }`}
                        >
                            <div className="flex items-start gap-1.5">
                                {f.traffic === 'red' ? (
                                    <AlertOctagon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-600" />
                                ) : f.traffic === 'yellow' ? (
                                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
                                ) : (
                                    // Kein Warndreieck: das Symbol allein macht aus
                                    // einer Zahl einen Vorwurf.
                                    <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                                )}
                                <div>
                                    <p
                                        className={`font-medium ${
                                            f.traffic === 'red'
                                                ? 'text-red-800'
                                                : f.traffic === 'yellow'
                                                  ? 'text-amber-900'
                                                  : 'text-slate-700'
                                        }`}
                                    >
                                        {f.title}
                                    </p>
                                    <p className="text-slate-600">{f.detail}</p>
                                    {f.projectName && (
                                        <p className="mt-0.5 text-slate-400">{f.projectName}</p>
                                    )}
                                    {/* Nur beim Berichtsbefund: es gibt Projekte, in
                                        denen ausschliesslich Leistungen erfasst und
                                        abgerechnet werden. Dort ist das leere Archiv
                                        kein Datenfehler, sondern die Arbeitsweise —
                                        und die Regel sperrte jede Rechnung. */}
                                    {!readOnly &&
                                        onExemptProject &&
                                        (f.rule === 'report_count_mismatch' ||
                                            f.rule === 'inspection_counter_mismatch') && (
                                            <button
                                                type="button"
                                                onClick={() => onExemptProject(true)}
                                                disabled={exempting}
                                                className="mt-1.5 inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                            >
                                                {exempting && (
                                                    <Loader2 className="h-3 w-3 animate-spin" />
                                                )}
                                                Für dieses Projekt werden keine Berichte erstellt
                                            </button>
                                        )}
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {result?.traffic === 'red' && (
                <p className="mt-2 text-xs font-medium text-red-700">
                    Rote Befunde sperren das Ausstellen. Sie lassen sich nicht wegquittieren — die
                    Projektdaten müssen zuerst berichtigt werden.
                </p>
            )}

            {result?.requiresAcknowledgement && !readOnly && (
                <div className="mt-2">
                    <label className="block text-xs">
                        <span className="mb-0.5 block font-medium text-amber-900">
                            Kenntnisnahme (Pflicht bei gelber Ampel)
                        </span>
                        <textarea
                            value={acknowledgement}
                            onChange={(e) => onAcknowledgementChange(e.target.value)}
                            rows={2}
                            placeholder="Warum darf trotz der Befunde abgerechnet werden?"
                            className="w-full rounded border border-amber-300 px-2 py-1"
                        />
                    </label>
                    <p className="mt-1 text-[11px] text-slate-500">
                        Die Begründung wird mit der Rechnung festgeschrieben und im Änderungsprotokoll
                        vermerkt.
                    </p>
                </div>
            )}

            {readOnly && result?.requiresAcknowledgement && acknowledgement && (
                <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">
                    <span className="font-medium">Kenntnisnahme:</span> {acknowledgement}
                </div>
            )}
        </div>
    );
};

/** Sammelaktion: eine Zeile je Projekt für den Read-only-Lauf. */
export const ProjectCheckSweepRow: React.FC<{
    projectName: string;
    result: ProjectCheckResult;
    onOpen?: () => void;
}> = ({ projectName, result, onOpen }) => {
    // Dieselbe Trennung wie im Panel: die Kurzfassung des Laufs soll ein
    // Projekt mit offenen Sicherheitseinbehalten nicht als auffaellig fuehren.
    const { issues } = splitCheckFindings(result.findings);
    return (
        <tr className="border-b border-slate-100">
            <td className="px-3 py-2">
                <ProjectCheckChip traffic={result.traffic} count={issues.length} />
            </td>
            <td className="px-3 py-2 font-medium text-slate-800">{projectName}</td>
            <td className="px-3 py-2 text-xs text-slate-600">
                {issues
                    .slice(0, 3)
                    .map((f) => f.title)
                    .join(' · ') || 'Keine Auffälligkeiten'}
                {issues.length > 3 && ` … (+${issues.length - 3})`}
            </td>
            <td className="px-3 py-2 text-right">
                {onOpen && (
                    <Button variant="secondary" onClick={onOpen} className="!px-2 !py-1 !text-xs">
                        Projekt öffnen
                    </Button>
                )}
            </td>
        </tr>
    );
};
